import {
    Badge,
    Box,
    Button,
    Center,
    Divider,
    Editable,
    EditableInput,
    EditablePreview,
    Image,
    Link,
    SimpleGrid,
    Spacer,
    StackDivider,
    Text,
    useColorModeValue,
    VStack,
} from "@chakra-ui/react"
import { MarketStatus, Position } from "@perp/sdk-curie"
import { LiveStats } from "component/LiveStats"
import { MarketListIcon } from "component/MarketListIcon"
import { ModalType } from "component/Modal/type"
import { MotionBox, MotionHStack, MotionStack } from "component/Motion"
import { Stats } from "component/Stats"
import { useModal } from "container/modal"
import { motion } from "framer-motion"
import { FundingPaymentPendingOf } from "module/history/component/FundingPaymentPending"
import { RiskBar } from "module/home/component/PageHome/RiskCard/RiskBar"
import { usePositionDetailStrings } from "module/position/hook/usePositionDetailStrings"
import { useAccountBalanceStrings } from "module/vault/hook/useAccountBalanceStrings"
import { useCallback, useRef, useState } from "react"
import useDimensions from "react-cool-dimensions"
import { useTranslation } from "react-i18next"
import { QRCode } from "react-qrcode-logo"
import { usePositionDetail } from "sdk-react/clearingHouse/usePositionDetail"
import { useMarket } from "sdk-react/markets/useMarket"
import { trackShowedClosePositionDialog } from "service/segment/tracks"
import { useMarketStrings } from "../../../hook/useMarketStrings"

interface PositionDetailProps {
    position: Position
}

const PositionBadge = ({ isLong }: { isLong: boolean }) => {
    const { t } = useTranslation()

    return (
        <Badge size="md" variant="subtle" colorScheme={isLong ? "green" : "red"}>
            {isLong ? t("position.side_long") : t("position.side_short")}
        </Badge>
    )
}

// const PureCSSPositionBadge
export function PositionDetail({ position }: PositionDetailProps) {
    const { status: marketStatus } = useMarket(position.market.tickerSymbol)
    const isMarketDisabled = marketStatus !== MarketStatus.ACTIVE
    const { indexPriceStr } = useMarketStrings(position.market.tickerSymbol)

    const { open: openModalPositionClose } = useModal(ModalType.POSITION_CLOSE)
    const openPositionCloseModal = useCallback(() => {
        trackShowedClosePositionDialog()
        openModalPositionClose({ position })
    }, [openModalPositionClose, position])
    const { isLongPosition, notionalValueStr, entryPriceStr, positionSizeStr, liquidationPriceStr } =
        usePositionDetailStrings(position)

    const { notionalValue, unrealizedPnl } = usePositionDetail(position)
    const { marginRatioStr, leverageStr } = useAccountBalanceStrings()

    // use-cool-dimensions
    const { width, observe } = useDimensions()

    // mask sensitive info when sharing
    const isShareable = false

    // calculate PnL percentage
    // entry notion value = position size * entry price (let's call it `entryNotionValue`
    // pnl % is unrealizedPnl / `entryNotionValue` * 100%
    const entryNotionValue = position.sizeAbs.times(position.entryPrice)
    const pnlPercentage = notionalValue && unrealizedPnl && unrealizedPnl.div(entryNotionValue).times(100)

    // toggle sharable
    // download screenshot
    const screenshotRef = useRef<HTMLDivElement>(null)
    const isVertical = width < 570

    // get random image from 1 to 8
    const getRandomImage = () => {
        const randomNumber = Math.floor(Math.random() * 6) + 1
        return `/assets/pnlcard/${randomNumber}.png`
    }
    const [bgUrl, setBgUrl] = useState(`/assets/pnlcard/1.png`)

    // pnl share meme text draggable
    const constraintsRef = useRef(null)

    const { t } = useTranslation()
    const subText = useColorModeValue("gray.500", "gray.400")
    // TODO: implement referral url
    const referralURL = ""

    // best possible pnl
    const { marketPrice } = useMarket(position.market.tickerSymbol)

    const positionValueByMarketPrice = position.sizeAbs.mul(marketPrice || 0)

    const bestPossiblePnl =
        position.side === "LONG"
            ? positionValueByMarketPrice.minus(entryNotionValue)
            : entryNotionValue.minus(positionValueByMarketPrice)

    return (
        <Center ref={observe} minH="100%" overflow={"visible"}>
            <Box w="100%" pb={8} ref={screenshotRef} px={5} pt={isShareable ? 5 : 0} mt={isShareable ? "-10px" : 0}>
                <SimpleGrid w="100%" columns={isVertical ? 1 : 2} spacingX={10} spacingY={4}>
                    <MotionStack align={"stretch"} minH={8} minW="100%" spacing={4} divider={<StackDivider />}>
                        <MotionHStack spacing={4} justify="center">
                            <MarketListIcon coin={position.market.baseSymbol} boxSize="28px" />
                            {isShareable && (
                                <VStack align="flex-start" spacing={-1}>
                                    <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold">
                                        {position.market.tickerSymbol}
                                    </Text>
                                    <Text color="gray.500" fontSize="sm">
                                        Perpetual Futures
                                    </Text>
                                </VStack>
                            )}
                            {!isShareable && (
                                <VStack align="flex-start" spacing={-1}>
                                    <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold">
                                        <Text isTruncated maxWidth={"6em"} as="span">
                                            {positionSizeStr}
                                        </Text>
                                        <Text ml={"0.5em"} as="span" color={subText} fontSize="xs" fontWeight="normal">
                                            {position.market.baseSymbol}
                                        </Text>
                                    </Text>
                                    <Text color="gray.500" fontSize="sm">
                                        ${notionalValueStr}
                                    </Text>
                                </VStack>
                            )}
                            {width >= 320 && <Spacer />}

                            {width >= 10 && <PositionBadge isLong={isLongPosition} />}
                        </MotionHStack>
                        {!isShareable && (
                            <MotionHStack justify={"space-between"}>
                                <FundingPaymentPendingOf symbol={position.market.tickerSymbol} />
                            </MotionHStack>
                        )}
                        {isShareable && (
                            <VStack spacing={0}>
                                <Stats label="Avg. Open price" number={entryPriceStr} size="xs" variant="left" />
                                <Stats label="Index price" number={indexPriceStr} size="xs" variant="left" />
                                <Stats label="Leverage" number={leverageStr} size="xs" variant="left" />
                            </VStack>
                        )}
                        <MotionHStack spacing={1}>
                            {isShareable ? (
                                <LiveStats
                                    suffix="%"
                                    key="shareable_pnl_percentage"
                                    label={t("general.profit_loss") + " %"}
                                    number={pnlPercentage ? pnlPercentage.toNumber() : 0}
                                    precision={1}
                                    showPnlColor
                                    size="2xl"
                                />
                            ) : (
                                <LiveStats
                                    key="pnl"
                                    label={t("general.profit_loss")}
                                    number={unrealizedPnl ? unrealizedPnl.toNumber() : 0}
                                    precision={1}
                                    showPnlColor
                                    size="xl"
                                    helper={`Best possible PnL: ${bestPossiblePnl.toFixed(4)} (using Smart Maker)`}
                                />
                            )}

                            <Spacer />

                            {isShareable && !referralURL && (
                                <QRCode
                                    quietZone={0}
                                    size={50}
                                    bgColor="rgba(34,35,36,1)"
                                    qrStyle="dots"
                                    eyeRadius={1}
                                    eyeColor={[
                                        "rgba(64,200,127,1)", // top/left eye
                                        "rgba(8,215,255,1)", // top/right eye
                                        "rgba(255,169,49,1)", // bottom/left eye
                                    ]}
                                    fgColor="rgba(255,255,255,1)"
                                    value={"https://app.perp.com"}
                                />
                            )}

                            {!isShareable && (
                                <MotionHStack>
                                    <MotionBox>
                                        <Button
                                            colorScheme="perpetual"
                                            size={"sm"}
                                            onClick={openPositionCloseModal}
                                            isDisabled={isMarketDisabled}
                                        >
                                            {width < 620
                                                ? t("position.close_position_short")
                                                : t("position.close_position")}
                                        </Button>
                                    </MotionBox>
                                </MotionHStack>
                            )}
                        </MotionHStack>
                    </MotionStack>
                    {isVertical && !isShareable && <Divider />}
                    {!isShareable && (
                        <MotionStack divider={<StackDivider />} spacing={4}>
                            <MotionStack>
                                <Stats label="Avg. Open price" number={entryPriceStr} size="xs" variant="left" />{" "}
                                <Stats label="Index price" number={indexPriceStr} size="xs" variant="left" />
                                <Stats
                                    size="xs"
                                    variant="left"
                                    label="Liquidation price"
                                    number={liquidationPriceStr}
                                />
                            </MotionStack>
                            <MotionStack flexGrow={1}>
                                <Stats
                                    label="Margin Ratio"
                                    number={marginRatioStr}
                                    showTooltip
                                    size="xs"
                                    variant="left"
                                />
                                <Stats size="xs" variant="left" label="Leverage" number={leverageStr} />
                                {!isVertical && <Spacer />}
                                <MotionBox pb={2}>
                                    <RiskBar heightX={0.5} />
                                </MotionBox>
                            </MotionStack>
                        </MotionStack>
                    )}
                    {isShareable && (
                        <Box>
                            <Text
                                mt={isVertical ? 0 : "-24px"}
                                pb={1}
                                userSelect={"none"}
                                fontSize="xs"
                                fontWeight="normal"
                                color="gray.400"
                                data-html2canvas-ignore
                            >
                                Yes, click to edit. I meme it.
                            </Text>
                            <MotionBox
                                ref={constraintsRef}
                                flexGrow={1}
                                h="230px"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                lineHeight={1.2}
                                textAlign={"center"}
                                borderRadius="xl"
                                bgColor="gray.400"
                                bgImage={bgUrl}
                                bgRepeat="no-repeat"
                                bgSize={"cover"}
                                px={3}
                                position="relative"
                                pt={5}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setBgUrl(getRandomImage())}
                            >
                                <motion.div ref={constraintsRef}>
                                    <MotionBox position={"absolute"} bottom={2} left={2}>
                                        <Image h="20px" src="/assets/pnlcard/perp-logo.svg" />
                                    </MotionBox>
                                    <MotionBox
                                        drag
                                        dragConstraints={constraintsRef}
                                        whileDrag={{ scale: 1.2 }}
                                        color="white"
                                        fontWeight="900"
                                        fontSize={"lg"}
                                        sx={{
                                            textShadow:
                                                "3px  3px 0 #000,-1px -1px 0 #000,1px -1px 0 #000,-1px  1px 0 #000,1px  1px 0 #000",
                                        }}
                                    >
                                        <Editable defaultValue="Look what I got on Perpetual Protocol. This is not even a NFT.">
                                            <EditablePreview userSelect={"none"} />
                                            <EditableInput p={2} bg="blackAlpha.500" />
                                        </Editable>
                                    </MotionBox>
                                </motion.div>
                            </MotionBox>
                        </Box>
                    )}
                </SimpleGrid>

                {/* The following section needs to integrate referral code */}
                {isShareable && referralURL && (
                    <MotionBox mt={8}>
                        <Divider />
                        <MotionStack mt={5} spacing={6} isInline={!isVertical}>
                            <QRCode
                                quietZone={0}
                                size={70}
                                logoImage={"/logo/logo-color.svg"}
                                removeQrCodeBehindLogo={true}
                                bgColor="rgba(34,35,36,1)"
                                qrStyle="dots"
                                eyeRadius={5}
                                eyeColor={[
                                    "rgba(64,200,127,1)", // top/left eye
                                    "rgba(8,215,255,1)", // top/right eye
                                    "rgba(255,169,49,1)", // bottom/left eye
                                ]}
                                fgColor="rgba(255,255,255,1)"
                                value={referralURL}
                            />

                            <MotionBox>
                                <Text fontSize={"lg"} fontWeight="bold">
                                    Get 5% trading fee rebate
                                </Text>
                                <Text fontSize={"sm"}>{referralURL}</Text>
                                <Text
                                    as={Link}
                                    fontSize={"sm"}
                                    href={"https://token.perp.com/referral"}
                                    color={subText}
                                >
                                    Generate my referral code
                                </Text>
                            </MotionBox>
                        </MotionStack>
                    </MotionBox>
                )}
            </Box>
        </Center>
    )
}
